import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, Input, OnInit, signal, ViewChild } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { ValueChangedEvent } from "devextreme/ui/drop_down_box";
import { SelectionChangedEvent } from "devextreme/ui/list";
import { confirm } from "devextreme/ui/dialog";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DemoAssetService } from "../../services/demo-asset.service";
import { AssetCategoryViewModel } from "../../core/model/ContentManagement";
import { Properties } from "devextreme/ui/popup";
import { DxListTypes } from "devextreme-angular/ui/list";

@Component({
  selector: "brd-asset-category-editor",
  templateUrl: "./asset-category-editor.component.html",
  styleUrl: "./asset-category-editor.component.less",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AssetCategoryEditorComponent,
      multi: true
    }
  ],
  standalone: false
})
export class AssetCategoryEditorComponent implements ControlValueAccessor, OnInit {
  destroyRef = inject(DestroyRef);
  @Input() categories: AssetCategoryViewModel[] = [];
  @Input() validationStatus: "valid" | "invalid" = "valid";
  @Input() validationErrors: { type: string; message: string }[] = [];
  @ViewChild("popupContainer", { static: true }) popupContainer!: ElementRef;

  selectedCategory = 0;
  categoryElement = signal<AssetCategoryViewModel | null>(null);

  categoryDropDownOpened = false;
  onChange!: (arg0: number) => void;
  onTouched!: () => void;
  disabled = false;

  popupEditorVisible = signal(false);
  popupTitle = "Create new category";
  categoryName = "";
  categoryDescription = "";
  categoryColor = "#000000";
  categoryIcon = "";
  categoryId = 0;
  categoryOrder = 0;

  cancelButtonOptions = {
    text: "Cancel",
    type: "normal",
    stylingMode: "outline",
    onClick: (): void => {
      this.popupEditorVisible.set(false);
    }
  };

  saveButtonOptions = {
    text: "Save",
    stylingMode: "contained",
    onClick: (): void => {
      this.saveCategory();
    }
  };

  protected dropDownOptions?: Properties;

  constructor(
    private readonly demoAssetService: DemoAssetService,
    private elRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.dropDownOptions = {
      container: this.popupContainer.nativeElement,
      wrapperAttr: { class: "category-dropdown" },
      position: {
        of: this.elRef.nativeElement,
        at: "left bottom",
        my: "left top",
        offset: "0 0",
        collision: "fit"
      }
    };
  }

  writeValue(obj: number): void {
    this.selectedCategory = obj;
    const el = this.categories.find(x => x.id === obj);
    this.categoryElement.set(el ?? null);
  }
  registerOnChange(fn: (arg0: number) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onCategorySelectionChanged(e: ValueChangedEvent): void {
    this.updateSelctedCategory(e.value);
  }

  onCategoryListSelectionChanged(e: SelectionChangedEvent<AssetCategoryViewModel>): void {
    const selectedCategory = e.addedItems[0];
    if (!selectedCategory) return;

    this.updateSelctedCategory(selectedCategory.id);
    this.categoryDropDownOpened = false;
  }

  iconClick(e: Event, action: "ADD" | "EDIT" | "DELETE", category?: AssetCategoryViewModel): void {
    e.stopPropagation();

    switch (action) {
      case "ADD":
        this.popupEditorVisible.set(true);
        this.popupTitle = "Create new category";
        this.categoryName = "";
        this.categoryDescription = "";
        this.categoryColor = "#000000";
        this.categoryIcon = "";
        this.categoryId = 0;
        this.categoryOrder = Math.max(...this.categories.map(x => x.order)) + 1;
        break;
      case "EDIT":
        this.popupEditorVisible.set(true);
        this.popupTitle = `Edit category ${category!.name}`;
        this.categoryName = category!.name;
        this.categoryDescription = category!.description;
        this.categoryColor = category!.color;
        this.categoryIcon = category!.icon;
        this.categoryId = category!.id;
        this.categoryOrder = category!.order;
        break;
      case "DELETE":
        confirm(`Are you sure to delete <strong>${category!.name}</strong>?`, "Delete category").then(dialogResult => {
          if (dialogResult) {
            this.demoAssetService
              .deleteCategory(category!.id)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(res => {
                this.categories = this.categories.filter(x => x.id !== res.id);
                if (this.selectedCategory === res.id) {
                  this.updateSelctedCategory(0);
                }
              });
          }
        });
        break;
    }
  }

  saveCategory(): void {
    if (!this.categoryName || !this.categoryColor) return;

    const newCategory = {
      id: this.categoryId,
      name: this.categoryName,
      description: this.categoryDescription,
      color: this.categoryColor,
      icon: this.categoryIcon,
      order: this.categoryOrder
    };
    if (this.categoryId) {
      this.demoAssetService
        .updateCategory(this.categoryId, newCategory)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(res => {
          const index = this.categories.findIndex(x => x.id === res.id);
          this.categories[index] = res;
          this.updateSelctedCategory(res.id);
          this.popupEditorVisible.set(false);
        });
    } else {
      this.demoAssetService
        .addCategory(newCategory)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(res => {
          this.categories.push(res);
          this.updateSelctedCategory(res.id);
          this.popupEditorVisible.set(false);
        });
    }
  }

  onReorder(e: DxListTypes.ItemReorderedEvent): void {
    const data = [...(e.component.option("dataSource") as AssetCategoryViewModel[])];

    data.splice(e.fromIndex, 1);
    data.splice(e.toIndex, 0, e.itemData);
    e.component.option("dataSource", data);

    this.demoAssetService.updateCategoryOrders(data.map(x => x.id)).subscribe();
  }

  private updateSelctedCategory(selectedCategory: number): void {
    this.selectedCategory = selectedCategory;
    const el = this.categories.find(x => x.id === selectedCategory);
    this.categoryElement.set(el ?? null);

    this.onChange(selectedCategory);
    this.onTouched();
  }
}
